import type React from "react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  followBlockLink,
  isViewportFragment,
} from "../../../lib/blockLinkNavigation";
import { getCohesiveBlockTokensCss } from "../../../lib/cohesiveBlockTokens";
import {
  BLOCK_RUNTIME_VERSION,
  COHESIVE_BLOCK_MESSAGE_SOURCE,
  injectIslandAttr,
  ISLAND_INJECTED_SELECTOR,
  readIslandInnerHtml,
  restoreInjectedIslandAttrs,
  type BlockEntryPoint,
  type BlockToParentMessage,
} from "../../../lib/htmlBlockRuntime";
import { ISLAND_EDIT_CSS } from "../../../lib/htmlIframeSrcDoc";
import {
  prepareStaticBlock,
  scopeBlockCss,
  type PreparedStaticBlock,
} from "../../../lib/staticBlock";
import { cn } from "../../../lib/utils";
import type { HtmlBlockCardCanvasProps } from "./HtmlBlockCardCanvas";
import { HtmlCardPlaceholder } from "./HtmlCardPlaceholder";

/**
 * A static block (`display.kind === "static"`), rendered in the host
 * document instead of an iframe.
 *
 * The body is sanitised, its stylesheets are scoped onto one wrapper, and
 * the result goes into a shadow root: the block keeps its own CSS namespace
 * (and the canvas keeps its own) without a document, a frame process, a
 * runtime bridge or an admission slot. The app's own webfonts resolve
 * because the host document already carries them.
 *
 * Islands are edited in place. The card plays the block runtime's part
 * itself: it raises the same messages (`ready`, `edit-request`,
 * `island-*`, `flush-request`, `exit-request`) through `onBlockMessage`, so
 * the host's edit session, autosave and region patch are the iframe card's,
 * unchanged.
 */

const ISLAND = "[data-edit-id]:not([data-no-edit])";
/** Same cap and debounce as the runtime script. */
const MAX_ISLAND_HTML_CHARS = 1_000_000;
const INPUT_DEBOUNCE_MS = 200;
const ENVELOPE = {
  source: COHESIVE_BLOCK_MESSAGE_SOURCE,
  v: BLOCK_RUNTIME_VERSION,
} as const;

/** The srcdoc path's island edit CSS, keyed on the wrapper instead of
 *  `html`. React Flow turns text selection off on nodes; islands need it. */
const EDIT_CSS =
  ISLAND_EDIT_CSS.split(ISLAND).join(
    `.block-root[data-cohesive-editing] ${ISLAND}`,
  ) +
  `.block-root[data-cohesive-editing] ${ISLAND}{-webkit-user-select:text;user-select:text}`;

/** The srcdoc path's hover tint: at rest (where a click on island text
 *  enters an edit session) and in interact-only mode. No shield sits over a
 *  static block, so plain `:hover` stands in for the runtime's tagging. */
const HOVER_CSS = `
  .block-root[data-cohesive-rest] ${ISLAND}:hover,
  .block-root[data-cohesive-interacting] ${ISLAND}:hover {
    background-color: rgba(99, 102, 241, 0.09);
    cursor: text;
    transition: background-color 0.15s;
  }
`;

const WRAPPER_CSS = `.block-root{contain:content;overflow:hidden;height:100%}`;

function staticTokensCss(): string {
  return scopeBlockCss(getCohesiveBlockTokensCss(), { rootSelector: ":host" });
}

/** One constructable sheet of block tokens for every static block on the
 *  page: `adoptedStyleSheets` shares the parsed rules across shadow roots. */
let tokensSheet: CSSStyleSheet | null | undefined;
function blockTokensSheet(): CSSStyleSheet | null {
  if (tokensSheet !== undefined) return tokensSheet;
  try {
    const sheet = new CSSStyleSheet();
    // `:root` would match nothing inside a shadow root — the custom
    // properties go on the host, where the subtree inherits them.
    sheet.replaceSync(staticTokensCss());
    tokensSheet = sheet;
  } catch {
    tokensSheet = null;
  }
  return tokensSheet;
}

export interface HtmlStaticBlockCardProps
  extends Pick<
    HtmlBlockCardCanvasProps,
    | "display"
    | "theme"
    | "transformHtml"
    | "previewImageUrl"
    | "selected"
    | "focused"
    | "resizing"
    | "isInteractive"
    | "islandsEditable"
    | "contentFrozen"
    | "editFocusPoint"
    | "onRuntimeReady"
    | "onBlockMessage"
    | "onNavigate"
    | "clickThrough"
    | "className"
    | "style"
    | "onDoubleClick"
    | "onDisplayReady"
  > {
  /** The block's body, as read by the card's text query. */
  body: string;
}

function islandOf(target: EventTarget | null | undefined): HTMLElement | null {
  const el =
    target instanceof Element
      ? target
      : target instanceof Node
        ? target.parentElement
        : null;
  return el?.closest<HTMLElement>(ISLAND) ?? null;
}

function stripSignatures(html: string): string {
  return html.replace(
    /([?&](?:amp;)?)(Expires|Signature|Key-Pair-Id|Policy)=[^&"'\s]*/g,
    "$1",
  );
}

/** The markup with every island emptied, and what each island held: two
 *  bodies with the same shape differ only in island text. */
function islandShape(html: string): {
  shape: string;
  regions: Map<string, string>;
} {
  const t = document.createElement("template");
  t.innerHTML = html;
  const regions = new Map<string, string>();
  for (const el of Array.from(t.content.querySelectorAll("[data-edit-id]"))) {
    regions.set(el.getAttribute("data-edit-id") ?? "", el.innerHTML);
    el.innerHTML = "";
  }
  return { shape: t.innerHTML, regions };
}

type CaretDocument = Document & {
  caretPositionFromPoint?: (
    x: number,
    y: number,
    options?: { shadowRoots?: ShadowRoot[] },
  ) => { offsetNode: Node; offset: number } | null;
  caretRangeFromPoint?: (x: number, y: number) => Range | null;
};

/** A collapsed range at a viewport point inside the shadow root. Only
 *  `caretPositionFromPoint` with `shadowRoots` reaches into it; WebKit's
 *  `caretRangeFromPoint` stops at the host, and the caller then keeps the
 *  focus without a caret position. */
function caretRangeAt(shadow: ShadowRoot, x: number, y: number): Range | null {
  const doc = document as CaretDocument;
  if (doc.caretPositionFromPoint) {
    const pos = doc.caretPositionFromPoint(x, y, { shadowRoots: [shadow] });
    if (!pos) return null;
    const range = document.createRange();
    range.setStart(pos.offsetNode, pos.offset);
    range.collapse(true);
    return range;
  }
  return doc.caretRangeFromPoint?.(x, y) ?? null;
}

export function HtmlStaticBlockCard({
  body,
  display,
  theme = "light",
  transformHtml,
  previewImageUrl = null,
  selected,
  focused = false,
  resizing = false,
  isInteractive = false,
  islandsEditable = false,
  contentFrozen = false,
  editFocusPoint = null,
  onRuntimeReady,
  onBlockMessage,
  onNavigate,
  clickThrough = false,
  className,
  style,
  onDoubleClick,
  onDisplayReady,
}: HtmlStaticBlockCardProps) {
  const frameless = display.chromeless;
  const transparent = display.chromeless;
  const live = (isInteractive || islandsEditable) && !resizing;
  const atRest = clickThrough && !live;
  const interacting = isInteractive && !islandsEditable;

  const hostRef = useRef<HTMLDivElement>(null);
  const shadowRef = useRef<ShadowRoot | null>(null);
  const rootRef = useRef<HTMLElement | null>(null);
  const [prepared, setPrepared] = useState<PreparedStaticBlock | null>(null);
  const renderedRef = useRef<PreparedStaticBlock | null>(null);
  // A body that arrived mid-session, applied when the session lets go.
  const stashRef = useRef<PreparedStaticBlock | null>(null);
  const timersRef = useRef(
    new Map<string, { t: ReturnType<typeof setTimeout>; el: HTMLElement }>(),
  );
  const lastFocusPointKeyRef = useRef<string | null>(null);
  const pairConsumedAtRef = useRef(0);
  const clickPointerDownRef = useRef<{ x: number; y: number } | null>(null);

  const editingRef = useRef(islandsEditable);
  editingRef.current = islandsEditable;
  const focusedRef = useRef(focused);
  focusedRef.current = focused;
  const modeRef = useRef({ atRest, interacting });
  modeRef.current = { atRest, interacting };
  const settledRef = useRef(false);
  const frozenRef = useRef(contentFrozen);
  frozenRef.current = contentFrozen;
  const darkRef = useRef(theme === "dark");
  darkRef.current = theme === "dark";
  const onBlockMessageRef = useRef(onBlockMessage);
  onBlockMessageRef.current = onBlockMessage;
  const onNavigateRef = useRef(onNavigate);
  onNavigateRef.current = onNavigate;
  const onRuntimeReadyRef = useRef(onRuntimeReady);
  onRuntimeReadyRef.current = onRuntimeReady;
  const onDisplayReadyRef = useRef(onDisplayReady);
  onDisplayReadyRef.current = onDisplayReady;

  const post = useCallback((msg: BlockToParentMessage) => {
    onBlockMessageRef.current?.(msg);
  }, []);

  const postIsland = useCallback(
    (type: "island-focus" | "island-input" | "island-blur", el: Element) => {
      const regionId = el.getAttribute("data-edit-id");
      if (!regionId) return;
      const html = readIslandInnerHtml(el);
      if (html.length > MAX_ISLAND_HTML_CHARS) {
        console.warn("[static-block] island content too large to autosave");
        return;
      }
      post({ ...ENVELOPE, type, regionId, html });
    },
    [post],
  );

  const cancelPendingInput = useCallback((regionId: string) => {
    const pending = timersRef.current.get(regionId);
    if (!pending) return;
    clearTimeout(pending.t);
    timersRef.current.delete(regionId);
  }, []);

  // Still-debounced content goes out before whatever is posted next (a
  // flush, an exit), so the session never saves a stale copy.
  const flushPendingInputs = useCallback(() => {
    for (const [regionId, pending] of timersRef.current) {
      clearTimeout(pending.t);
      timersRef.current.delete(regionId);
      if (pending.el.isConnected) postIsland("island-input", pending.el);
    }
  }, [postIsland]);

  const applyEditable = useCallback((on: boolean) => {
    const root = rootRef.current;
    if (!root) return;
    root.toggleAttribute("data-cohesive-editing", on);
    for (const el of Array.from(root.querySelectorAll<HTMLElement>(ISLAND))) {
      if (on) {
        el.setAttribute("contenteditable", "true");
        el.setAttribute("spellcheck", "false");
        for (const button of Array.from(el.querySelectorAll("button"))) {
          injectIslandAttr(button, "tabindex", "-1");
          if (!button.getAttribute("type")) {
            injectIslandAttr(button, "type", "button");
          }
        }
      } else {
        el.removeAttribute("contenteditable");
        el.removeAttribute("spellcheck");
        for (const injected of Array.from(
          el.querySelectorAll(ISLAND_INJECTED_SELECTOR),
        )) {
          restoreInjectedIslandAttrs(injected);
        }
      }
    }
    if (!on) islandOf(shadowRef.current?.activeElement)?.blur();
  }, []);

  /** Content coordinates (the card's own CSS pixels) to the viewport. */
  const toViewport = useCallback((point: BlockEntryPoint) => {
    const host = hostRef.current;
    if (!host) return null;
    const rect = host.getBoundingClientRect();
    const scale = host.offsetWidth > 0 ? rect.width / host.offsetWidth : 1;
    return { x: rect.left + point.x * scale, y: rect.top + point.y * scale };
  }, []);

  const focusIslandAt = useCallback(
    (point: BlockEntryPoint) => {
      const shadow = shadowRef.current;
      const at = toViewport(point);
      if (!shadow || !at) return;
      const el = islandOf(shadow.elementFromPoint(at.x, at.y));
      // A click that landed once the island was editable already placed the
      // caret, and typing may have started.
      if (!el || shadow.activeElement === el) return;
      el.focus({ preventScroll: true });
      const range = caretRangeAt(shadow, at.x, at.y);
      if (range && el.contains(range.startContainer)) {
        // Not removeAllRanges + addRange: in a shadow root WebKit then keeps
        // focus but drops every keystroke.
        window
          .getSelection()
          ?.collapse(range.startContainer, range.startOffset);
      }
    },
    [toViewport],
  );

  // The runtime's document listeners, on the shadow root. Keys, clipboard
  // events and edits stop here while the caret is in an island: past the
  // shadow root they are retargeted to the host, where the canvas would
  // read a Backspace as "delete node" and a paste as "paste nodes".
  const attachListeners = useCallback(
    (shadow: ShadowRoot) => {
      const inEditedIsland = (e: Event) =>
        editingRef.current ? islandOf(e.target) : null;
      shadow.addEventListener("focusin", (e) => {
        const el = inEditedIsland(e);
        if (el) postIsland("island-focus", el);
      });
      shadow.addEventListener("focusout", (e) => {
        const el = inEditedIsland(e);
        if (!el) return;
        cancelPendingInput(el.getAttribute("data-edit-id") ?? "");
        postIsland("island-blur", el);
        // Focus moved to something else on the page. Null (a click on the
        // block's own plain content, the window losing focus) keeps the
        // session, as it does in a frame.
        // Only the canvas latch is left this way; the lightbox would read
        // an exit as "close".
        const next = (e as FocusEvent).relatedTarget;
        if (
          focusedRef.current &&
          next instanceof Node &&
          !shadow.contains(next)
        ) {
          post({ ...ENVELOPE, type: "exit-request", reason: "blur" });
        }
      });
      shadow.addEventListener("input", (e) => {
        const el = inEditedIsland(e);
        if (!el) return;
        const regionId = el.getAttribute("data-edit-id") ?? "";
        cancelPendingInput(regionId);
        timersRef.current.set(regionId, {
          el,
          t: setTimeout(() => {
            timersRef.current.delete(regionId);
            if (el.isConnected) postIsland("island-input", el);
          }, INPUT_DEBOUNCE_MS),
        });
      });
      shadow.addEventListener("keydown", (e) => {
        const el = inEditedIsland(e);
        if (!el) return;
        const key = e as KeyboardEvent;
        if (key.key === "Escape") {
          el.blur();
          post({ ...ENVELOPE, type: "exit-request", reason: "escape" });
          key.preventDefault();
          key.stopPropagation();
          return;
        }
        key.stopPropagation();
        if (key.key === "Enter" && !key.shiftKey) {
          key.preventDefault();
          document.execCommand("insertLineBreak");
        }
        if (key.key.toLowerCase() === "s" && (key.metaKey || key.ctrlKey)) {
          key.preventDefault();
          flushPendingInputs();
          post({ ...ENVELOPE, type: "flush-request" });
        }
      });
      for (const type of ["keyup", "keypress", "copy", "cut"]) {
        shadow.addEventListener(type, (e) => {
          if (inEditedIsland(e)) e.stopPropagation();
        });
      }
      shadow.addEventListener("paste", (e) => {
        if (!inEditedIsland(e)) return;
        e.preventDefault();
        e.stopPropagation();
        const text =
          (e as ClipboardEvent).clipboardData?.getData("text/plain") ?? "";
        if (text) document.execCommand("insertText", false, text);
      });
      // Controls and links inside an island take the caret, not the action.
      shadow.addEventListener(
        "mousedown",
        (e) => {
          if (!inEditedIsland(e)) return;
          const t = e.target instanceof Element ? e.target : null;
          if (t?.closest("button, input, select, textarea")) e.preventDefault();
        },
        true,
      );
      // Links never navigate the host page natively. As with the iframe
      // card, an `#id` fragment scrolls within the block; a viewport
      // fragment (`#x=…`) and every other link go through followBlockLink:
      // same-origin to the host's onNavigate, https otherwise to a new tab.
      shadow.addEventListener(
        "click",
        (e) => {
          const t = e.target instanceof Element ? e.target : null;
          if (inEditedIsland(e)) {
            if (t?.closest("a, button, input, select, textarea")) {
              e.preventDefault();
            }
            return;
          }
          const a = t?.closest<HTMLAnchorElement>("a");
          if (!a) return;
          e.preventDefault();
          const href = a.getAttribute("href");
          if (!href || href === "#") return;
          if (href.startsWith("#") && !isViewportFragment(href)) {
            shadow
              .getElementById(href.slice(1))
              ?.scrollIntoView({ behavior: "smooth", block: "start" });
            return;
          }
          followBlockLink(href, onNavigateRef.current);
        },
        true,
      );
    },
    [cancelPendingInput, flushPendingInputs, post, postIsland],
  );

  const applyModeAttrs = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    root.toggleAttribute("data-cohesive-rest", modeRef.current.atRest);
    root.toggleAttribute(
      "data-cohesive-interacting",
      modeRef.current.interacting,
    );
  }, []);

  // The runtime's `content-size` (the root clips at the box, so its scroll
  // extent is the natural height) and its one-time `settled`, after fonts.
  const reportSize = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    post({
      ...ENVELOPE,
      type: "content-size",
      width: root.clientWidth,
      height: root.scrollHeight,
    });
  }, [post]);
  const reportSettled = useCallback(() => {
    if (settledRef.current) return;
    settledRef.current = true;
    const fonts =
      "fonts" in document ? document.fonts.ready : Promise.resolve();
    void fonts.then(() =>
      requestAnimationFrame(() => {
        reportSize();
        post({ ...ENVELOPE, type: "settled" });
      }),
    );
  }, [post, reportSize]);

  const commit = useCallback(
    (next: PreparedStaticBlock) => {
      const host = hostRef.current;
      if (!host) return;
      let shadow = shadowRef.current;
      if (!shadow) {
        shadow = host.shadowRoot ?? host.attachShadow({ mode: "open" });
        shadowRef.current = shadow;
        const sheet = blockTokensSheet();
        if (sheet) shadow.adoptedStyleSheets = [sheet];
        attachListeners(shadow);
      }
      const prev = renderedRef.current;
      const root = rootRef.current;
      const nextShape = islandShape(next.html);
      if (
        prev &&
        root &&
        prev.css === next.css &&
        islandShape(prev.html).shape === nextShape.shape
      ) {
        // Island text only: in place, leaving the island being typed in and
        // any island that already reads the same (the user's own edit, back
        // with fresh links) alone.
        const active = islandOf(shadow.activeElement);
        let touched = false;
        for (const el of Array.from(root.querySelectorAll("[data-edit-id]"))) {
          const html = nextShape.regions.get(
            el.getAttribute("data-edit-id") ?? "",
          );
          if (html === undefined || el === active) continue;
          if (
            stripSignatures(readIslandInnerHtml(el)) === stripSignatures(html)
          ) {
            continue;
          }
          el.innerHTML = html;
          touched = true;
        }
        if (touched && editingRef.current) applyEditable(true);
      } else {
        // Style text goes in through textContent, never markup: a `</style>`
        // in it would otherwise close the tag and inject into the page.
        const styles = [
          `${next.css}\n${WRAPPER_CSS}\n${EDIT_CSS}\n${HOVER_CSS}`,
        ];
        if (!blockTokensSheet()) styles.unshift(staticTokensCss());
        // The card plate behind the shadow root carries the background; the
        // block's own root stays transparent either way.
        const root = document.createElement("div");
        root.className = darkRef.current ? "block-root dark" : "block-root";
        root.innerHTML = next.html;
        shadow.replaceChildren(
          ...styles.map((css) => {
            const style = document.createElement("style");
            style.textContent = css;
            return style;
          }),
          root,
        );
        rootRef.current = root;
        applyModeAttrs();
        if (editingRef.current) applyEditable(true);
      }
      renderedRef.current = next;
      onRuntimeReadyRef.current?.({
        islands: rootRef.current?.querySelectorAll(ISLAND).length ?? 0,
      });
      onDisplayReadyRef.current?.();
      reportSize();
      reportSettled();
    },
    [applyEditable, applyModeAttrs, attachListeners, reportSize, reportSettled],
  );

  // The presign rewrite is async and may hand back a new identity when the
  // links are renewed; the sanitise is cheap enough to redo with it.
  useEffect(() => {
    let current = true;
    void Promise.resolve(transformHtml ? transformHtml(body) : body)
      .then((transformed) => {
        if (current && transformed != null)
          setPrepared(prepareStaticBlock(transformed));
      })
      .catch(() => {
        /* a rewrite that failed: the card keeps what it has */
      });
    return () => {
      current = false;
    };
  }, [body, transformHtml]);

  useEffect(() => {
    if (!prepared) return;
    if (frozenRef.current && renderedRef.current) {
      stashRef.current = prepared;
      return;
    }
    commit(prepared);
  }, [prepared, commit]);

  useEffect(() => {
    if (contentFrozen || !stashRef.current) return;
    const next = stashRef.current;
    stashRef.current = null;
    commit(next);
  }, [contentFrozen, commit]);

  const dark = theme === "dark";
  useEffect(applyModeAttrs, [atRest, interacting, applyModeAttrs]);

  // A theme flip must not rebuild the subtree.
  useEffect(() => {
    rootRef.current?.classList.toggle("dark", dark);
  }, [dark]);

  // A layout effect, so pending input reaches the session before the
  // node's falling-edge flush (a parent effect, which runs after this).
  useLayoutEffect(() => {
    if (islandsEditable) {
      applyEditable(true);
      if (!editFocusPoint) return;
      const key = `${editFocusPoint.x},${editFocusPoint.y}`;
      if (key === lastFocusPointKeyRef.current) return;
      // After the host's session-start effects, which clear the island
      // store: an `island-focus` posted now would set a baseline that is
      // then wiped, and the first input would become the baseline instead.
      // A frame's focus arrives a message round trip later for the same
      // reason.
      const point = editFocusPoint;
      const timer = setTimeout(() => {
        if (!editingRef.current) return;
        lastFocusPointKeyRef.current = key;
        focusIslandAt(point);
      }, 0);
      return () => clearTimeout(timer);
    } else {
      flushPendingInputs();
      applyEditable(false);
      lastFocusPointKeyRef.current = null;
    }
  }, [
    islandsEditable,
    editFocusPoint,
    applyEditable,
    focusIslandAt,
    flushPendingInputs,
  ]);

  useEffect(
    () => () => {
      if (!editingRef.current) {
        for (const pending of timersRef.current.values()) {
          clearTimeout(pending.t);
        }
        timersRef.current.clear();
        return;
      }
      flushPendingInputs();
      post({ ...ENVELOPE, type: "flush-request" });
    },
    [flushPendingInputs, post],
  );

  const contentPoint = useCallback(
    (event: React.MouseEvent): BlockEntryPoint | null => {
      const host = hostRef.current;
      if (!host) return null;
      const rect = host.getBoundingClientRect();
      const scale = host.offsetWidth > 0 ? rect.width / host.offsetWidth : 1;
      return {
        x: (event.clientX - rect.left) / scale,
        y: (event.clientY - rect.top) / scale,
      };
    },
    [],
  );

  // At rest a click on island text opens an edit session with the caret
  // there — the iframe card's click-through, with the same gesture guards.
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!clickThrough || live || event.detail !== 1) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      const down = clickPointerDownRef.current;
      if (
        down &&
        (Math.abs(event.clientX - down.x) > 4 ||
          Math.abs(event.clientY - down.y) > 4)
      ) {
        return;
      }
      const target = event.nativeEvent.composedPath()[0];
      // A link or control acts and opens no edit, but still consumes the
      // pair, as an interactive click-result does for the iframe card.
      if (
        target instanceof Element &&
        target.closest("a, button, input, select, textarea")
      ) {
        pairConsumedAtRef.current = Date.now();
        return;
      }
      if (!islandOf(target)) return;
      const point = contentPoint(event);
      if (!point) return;
      pairConsumedAtRef.current = Date.now();
      post({ ...ENVELOPE, type: "edit-request", ...point });
    },
    [clickThrough, live, contentPoint, post],
  );

  // Double-click opens the detail pane, except when its first click went
  // into the block: the frame swallows that pair, so this card does too.
  const handleDoubleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (live || Date.now() - pairConsumedAtRef.current < 500) return;
      onDoubleClick?.(contentPoint(event) ?? undefined);
    },
    [live, onDoubleClick, contentPoint],
  );

  return (
    <div
      className={cn(
        "group relative h-full w-full overflow-hidden",
        !frameless && "rounded-xl",
        selected && !focused && "ring-2 ring-canvas-selection/60",
        // Inside the block the pointer is the content's, as over a live
        // frame: no node drag, no pan, no wheel zoom.
        live && "nodrag nopan nowheel",
        className,
      )}
      style={{ colorScheme: theme, ...style }}
      onPointerDownCapture={(event) => {
        clickPointerDownRef.current = { x: event.clientX, y: event.clientY };
      }}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      data-static-block="1"
    >
      {!prepared && (
        <HtmlCardPlaceholder
          previewImageUrl={transparent ? null : previewImageUrl}
          showPlate={!frameless}
        />
      )}
      <div
        className={cn(
          "absolute inset-0",
          prepared ? "opacity-100" : "opacity-0",
          !transparent && !frameless && "bg-card",
        )}
        style={{ colorScheme: theme }}
      >
        <div
          ref={hostRef}
          className="absolute inset-0 h-full w-full"
          data-static-host=""
        />
      </div>
    </div>
  );
}
